import i18n from '../i18n/config';
import { apiConfig } from '../config/api';
import {
  AuthenticationRequest,
  PlatformAuthResponse,
  ApiErrorResponse,
  ClientConfigurationResponse
} from '../types/auth';

/**
 * API Service for communicating with Platform.IdentityServer backend
 */
export class ApiService {
  private readonly baseUrl: string;
  private readonly timeout: number;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
    this.timeout = apiConfig.timeout;
  }

  /**
   * Make HTTP request with error handling
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    clientId?: string
  ): Promise<T> {
    let url = `${this.baseUrl}${endpoint}`;
    
    // Add client_id to query parameters if provided
    if (clientId) {
      const separator = endpoint.includes('?') ? '&' : '?';
      url = `${url}${separator}client_id=${encodeURIComponent(clientId)}`;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // No runtime params survive a bare key, so this fallback is localized
        // here; a backend-provided `message` (verbatim) takes precedence.
        let errorMessage = i18n.t('errors.api.httpError', {
          status: response.status,
          statusText: response.statusText
        });

        try {
          const errorData: ApiErrorResponse = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If we can't parse error response, use default message
        }

        throw new Error(errorMessage);
      }

      // Handle empty responses (like 204 No Content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('errors.api.timeout');
        }
        throw error;
      }

      throw new Error('errors.api.unexpected');
    }
  }

  /**
   * Exchange Firebase token for platform JWT token
   */
  async authenticateWithFirebaseToken(
    firebaseToken: string,
    clientId: string,
    providerId: 'google' | 'apple' | 'email',
    returnUrl?: string,
    metadata?: Record<string, string>
  ): Promise<PlatformAuthResponse> {
    // Capitalize providerId to match backend enum values
    const capitalizedProviderId = providerId.charAt(0).toUpperCase() + providerId.slice(1) as 'Google' | 'Apple' | 'Email';
    
    const request: AuthenticationRequest = {
      providerId: capitalizedProviderId,
      firebaseToken,
      returnUrl,
      metadata: metadata || {}
    };

    return this.makeRequest<PlatformAuthResponse>('/api/auth/authenticate', {
      method: 'POST',
      body: JSON.stringify(request)
    }, clientId);
  }

  /**
   * Sign out user by revoking Firebase token
   */
  async signOut(firebaseToken: string, clientId: string): Promise<void> {
    const request = {
      firebaseToken
    };

    await this.makeRequest<void>('/api/auth/signout', {
      method: 'POST',
      body: JSON.stringify(request)
    }, clientId);
  }

  /**
   * Initiate password reset
   */
  async initiatePasswordReset(email: string, clientId: string): Promise<void> {
    const request = {
      email
    };

    await this.makeRequest<void>('/api/auth/password-reset', {
      method: 'POST',
      body: JSON.stringify(request)
    }, clientId);
  }

  /**
   * Validate return URL
   */
  async validateReturnUrl(returnUrl: string, clientId: string): Promise<{ isValid: boolean; error?: string }> {
    const request = {
      returnUrl
    };

    return this.makeRequest<{ isValid: boolean; error?: string }>('/api/auth/validate-return-url', {
      method: 'POST',
      body: JSON.stringify(request)
    }, clientId);
  }


  /**
   * Get client configuration
   */
  async getClientConfiguration(clientId: string): Promise<ClientConfigurationResponse> {
    return this.makeRequest<ClientConfigurationResponse>('/api/auth/client-config', {
      method: 'GET'
    }, clientId);
  }
}

// Export singleton instance
export const apiService = new ApiService();